import React, { useEffect, useState } from "react";
import ForwardInstructions from "../../ForwardInstructions/ForwardInstructions";
import { markSetupStep } from "../../../lib/setupScore";
import { trackEvent } from "../../../lib/analytics";
import {
  normalizeConditions,
  isUnconditional,
} from "../../../lib/callForwardingCodes";

/*
 * GoLive Step 3 (visual Step 2) wrapper — Call Forwarding Setup.
 *
 * Renders the same underlying <ForwardInstructions/> as PostPurchase.
 * Per updated Figma:
 *   - The "I will publish AI Receptionist number directly / Skip this
 *     step" escape-hatch IS shown (hideDirectPublishCta=false). Earlier
 *     spec hid it for paid GoLive flow; new design treats it as a
 *     legitimate forward-without-forwarding shortcut.
 *   - Primary CTA copy "I have done this →" lives inside the
 *     ForwardInstructions component itself (its wizardMode default is
 *     "DO IT NOW" — copy update belongs there if needed).
 *
 * Setup score `callForwardingDone` (10 pts) fires here on the same
 * onWizardDone wrap pattern as the PostPurchase wrapper.
 */

export default function ForwardingStep({
  onNext,
  onBack,
  onSkip,
  agentCtx,
  // "When Call Forwarding?" selection from the Carrier step. Empty array =
  // legacy behaviour (single unconditional code).
  forwardingConditions = [],
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (agentCtx?.loading) return;
    if (agentCtx?.agentId)
      sessionStorage.setItem("SelectAgentId", agentCtx.agentId);
    if (agentCtx?.business?.businessId)
      sessionStorage.setItem(
        "SelectAgentBusinessId",
        agentCtx.business.businessId,
      );
    setReady(true);
  }, [agentCtx]);

  if (!ready) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
        Loading forwarding instructions…
      </div>
    );
  }

  const conditions = normalizeConditions(forwardingConditions);

  const handleDone = (method) => {
    markSetupStep("callForwardingDone", agentCtx?.agentId);
    // GA4: GO-LIVE GATE — carrier forwarding active (key event).
    trackEvent("call_forwarding_completed", {
      method: typeof method === "string" ? method : "dialed",
      conditions: conditions.join(","),
      condition_count: conditions.length,
      mode: conditions.length ? (isUnconditional(conditions) ? "unconditional" : "conditional") : "",
    });
    if (typeof onNext === "function") onNext();
  };

  // Resolve agent display name for the "I will publish {name}'s number
  // directly" skip-helper line on the Forwarding screen. PostPurchase
  // doesn't pass agentCtx through this wrapper, so we look it up from
  // the prop chain we already have.
  const resolvedAgentName =
    agentCtx?.agent?.agentName ||
    agentCtx?.agent?.name ||
    "Sophia";

  return (
    <ForwardInstructions
      wizardMode={true}
      hideDirectPublishCta={false}
      onWizardDone={handleDone}
      onWizardBack={onBack}
      // Forward agentCtx so ForwardInstructions' Layer-1 voip lookup
      // hits — without this, it falls through to its zustand store
      // fetch which is empty because GoLiveWizard's refresh path
      // doesn't go through the zustand setAgentById. Net effect was
      // a blank "voip not loaded" state right after the user tapped
      // Confirm Number on the previous step.
      agentCtx={agentCtx}
      agentName={resolvedAgentName}
      forwardingConditions={conditions}
    />
  );
}
